import type { ChargeStep } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import { CHARGE_STEPS } from '@/features/china-payment/chargeFields.js';

/**
 * Where the customer is in the request (MYP-109): 1 Amount · 2 Your details · 3 Pay and upload.
 * Shown, not clicked — each step's Back and Next move between them, so a later step can never be
 * reached with an earlier one unfinished. On a phone only the current step keeps its name.
 */
export function ChargeSteps({ current }: { current: ChargeStep }) {
  const { t } = useTranslation();
  const currentIndex = CHARGE_STEPS.indexOf(current);

  return (
    <ol
      aria-label={t('chinaPayment.form.steps.label')}
      // `relative`: the sr-only labels are placed against the list, never against a scroll box.
      className="relative flex flex-wrap items-center gap-1 sm:gap-2"
    >
      {CHARGE_STEPS.map((step, index) => {
        const isCurrent = index === currentIndex;
        const isDone = index < currentIndex;
        return (
          <li
            key={step}
            aria-current={isCurrent ? 'step' : undefined}
            className={cn(
              'flex min-h-8 items-center gap-1.5 text-sm sm:gap-2',
              isCurrent ? 'font-semibold text-ink' : 'text-muted',
            )}
          >
            {index > 0 ? (
              <span aria-hidden="true" className="mr-0.5 h-px w-3 bg-line-strong sm:mr-0 sm:w-6" />
            ) : null}
            <span
              aria-hidden="true"
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold tabular-nums',
                isDone
                  ? 'border-brand bg-brand text-white'
                  : isCurrent
                    ? 'border-brand text-brand'
                    : 'border-line-strong text-muted',
              )}
            >
              {isDone ? <Icon name="check" size={15} /> : index + 1}
            </span>
            <span className={cn(!isCurrent && 'sr-only sm:not-sr-only')}>
              {t(`chinaPayment.form.steps.${step}`)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
